'use client';
import React, { Suspense } from 'react';
import { SectionTitle } from '@benzinga/core-ui';
import type { Financials } from '@benzinga/securities-manager';
import type { Earnings } from '@benzinga/calendar-manager';
import type { News } from '@benzinga/basic-news-manager';

import type { BaseQuotePageProps } from '../../../../../src/utils/quote/types';
import type { EarningsSummary, GuidanceSummary } from '../../../../../src/utils/quote/summaries';
import QuotePageTitleLink from '../_components/QuotePageTitleLink';
import { EarningsChart } from '../../../../../src/components/Quote/EarningsCharts';
import GuidanceOverview from '../../../../../src/components/Quote/GuidanceOverview';
import RecentEarningsResultsTable, {
  ForwardEstimatesTable,
} from '../../../../../src/components/Quote/EarningsResultsTable';
import { QuoteNewsFeed } from '../../../../../src/components/Quote/QuoteNewsFeed';
import { QuoteFooter } from '../../../../../src/components/Quote/QuoteFooter';
import PeopleAlsoWatch from '../_components/PeopleAlsoWatch';

const FreestarAd = React.lazy(() =>
  import('@benzinga/ads').then(module => ({ default: module.FreestarAd })),
);

interface EarningsForecastsContentProps extends BaseQuotePageProps {
  earningsSummary: EarningsSummary | null;
  fundamentalsAnnual?: Financials | null;
  guidanceSummary: GuidanceSummary | null;
  forwardEarnings: Earnings[];
  earningsNews: News[];
}

const EarningsForecastsContent: React.FC<EarningsForecastsContentProps> = ({
  earningsSummary,
  guidanceSummary,
  forwardEarnings,
  earningsNews,
  metaProps,
  profile,
  symbol,
}) => {
  const companyName = profile?.richQuoteData?.name ?? symbol;
  const todayLocal = new Date();
  const todayISO = `${todayLocal.getFullYear()}-${String(todayLocal.getMonth() + 1).padStart(2, '0')}-${String(todayLocal.getDate()).padStart(2, '0')}`;
  const pastEarnings = (earningsSummary?.earnings ?? [])
    .filter(
      e =>
        !!e.date &&
        e.date.slice(0, 10) <= todayISO &&
        ((e.eps && e.eps !== '0') ||
          (e.eps_est && e.eps_est !== '0') ||
          (e.revenue && e.revenue !== '0') ||
          (e.revenue_est && e.revenue_est !== '0')),
    )
    .slice()
    .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));

  return (
    <>
      <div className="main-div">
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="flex flex-col gap-y-8 w-full lg:w-9/12">
            <div className="flex flex-col gap-2">
              <SectionTitle level={1} uppercase={false}>
                <QuotePageTitleLink title={metaProps.title} symbol={symbol} />
              </SectionTitle>
              <p className="section-description">{metaProps.description}</p>
            </div>

            {pastEarnings.length ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 w-full">
                <div className="flex flex-col gap-1 min-w-0">
                  <p className="self-end text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Earnings Per Share (Non-GAAP)
                  </p>
                  <React.Suspense fallback={<div className="h-56" />}>
                    <EarningsChart data={pastEarnings} type="eps" />
                  </React.Suspense>
                </div>
                <div className="flex flex-col gap-1 min-w-0">
                  <p className="self-end text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Quarterly Revenue (Billions)
                  </p>
                  <React.Suspense fallback={<div className="h-56" />}>
                    <EarningsChart data={pastEarnings} type="revenue" />
                  </React.Suspense>
                </div>
              </div>
            ) : null}

            {guidanceSummary && (
              <div className="flex flex-col gap-2">
                <SectionTitle level={2} size="xl" uppercase={false}>
                  {companyName} Guidance
                </SectionTitle>
                <GuidanceOverview guidanceSummary={guidanceSummary} quote={profile?.richQuoteData} />
              </div>
            )}

            {pastEarnings.length > 0 && (
              <div className="flex flex-col gap-2">
                <SectionTitle level={2} size="xl" uppercase={false}>
                  Recent Earnings Results
                </SectionTitle>
                <RecentEarningsResultsTable earnings={pastEarnings} />
              </div>
            )}

            <ForwardEstimatesTable forwardEarnings={forwardEarnings} />

            {earningsNews.length > 0 && (
              <div className="flex flex-col gap-2">
                <SectionTitle level={2} size="xl" uppercase={false}>
                  Earnings &amp; Guidance News
                </SectionTitle>
                <QuoteNewsFeed
                  nodes={earningsNews}
                  query={{ channels: [16888, 16889], pageSize: 6, tickers: symbol }}
                  seeMoreEnabled={true}
                  showLoadMoreButton={true}
                />
              </div>
            )}

            <QuoteFooter activeTab="earnings-forecasts" showAttribution={false} />
          </div>

          <PeopleAlsoWatch profile={profile} symbol={symbol}>
            <Suspense fallback={<div />}>
              <FreestarAd className="w-[300px] min-w-[300px]" onlyDesktop={true} placement="sidebar_2" />
            </Suspense>
          </PeopleAlsoWatch>
        </div>
      </div>
    </>
  );
};

export default EarningsForecastsContent;
